import PropTypes from 'prop-types';
import { twMerge } from 'tailwind-merge';

import Heading from 'shopper/components/Heading';
import Text from 'shopper/components/Text';

import ErrorBoundary from 'components/ErrorBoundary';

import useCouponPicker from 'hooks/useCouponPicker';
import useExclusiveCoupon from 'hooks/useExclusiveCoupon';

import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';
import { trackEvent } from 'lib/posthog';

import COUPONS from 'constants/coupons';

const getCouponCodeText = (couponCode, visibility) => {
  if (visibility === COUPONS.VISIBILITY_STATUS.APP_ONLY) {
    return COUPONS.BADGES.APP_ONLY;
  }

  return couponCode || COUPONS.COUPON_WITH_NO_CODE;
};

const ExpiredCouponCard = ({
  couponCode,
  couponId,
  couponTitle,
  gaEventCategory,
  storeName,
  storeSlug,
  visibility,
}) => {
  const {
    isCouponBlocked,
    formatGaEventActionByVisibility,
    onUnloggedUserTriesOpenExclusiveCoupon,
  } = useExclusiveCoupon();
  const { openCouponPicker } = useCouponPicker(gaEventCategory);

  const handleClick = () => {
    sendEvent({
      action: formatGaEventActionByVisibility('coupon_click', visibility),
      category: gaEventCategory,
    });

    if (isCouponBlocked(visibility)) {
      onUnloggedUserTriesOpenExclusiveCoupon(visibility, () => {
        openCouponPicker({ couponId, storeName, storeSlug });
      });
      return;
    }

    trackEvent('click coupon card', {
      id: couponId,
      visibility: 'expired',
    });

    openCouponPicker({ couponId, storeName, storeSlug });
  };

  return (
    <button
      className="flex flex-col gap-4 rounded-3 border border-solid border-neutral-high-300 bg-neutral-high-100 px-4 py-4 transition-all hover:opacity-60 dark:border-neutral-low-200 dark:bg-neutral-low-500 md:flex-row md:items-center md:justify-between"
      onClick={handleClick}
    >
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-6">
        <span className="w-fit rounded-full bg-neutral-high-400 px-2 py-1 text-xs text-neutral-high-100 dark:bg-neutral-low-200 dark:text-neutral-low-400">
          {placeholder('descriptions.expiredCoupon')}
        </span>
        <Text
          className="text-start text-neutral-low-200 dark:text-neutral-high-400"
          size="size1"
        >
          {couponTitle}
        </Text>
      </div>
      <Text
        className="shrink-0 text-neutral-low-400 line-through dark:text-neutral-high-200"
        size="size1"
        bold
      >
        {getCouponCodeText(couponCode, visibility)}
      </Text>
    </button>
  );
};

ExpiredCouponCard.propTypes = {
  couponCode: PropTypes.string,
  couponId: PropTypes.number.isRequired,
  couponTitle: PropTypes.string.isRequired,
  gaEventCategory: PropTypes.string.isRequired,
  storeName: PropTypes.string.isRequired,
  storeSlug: PropTypes.string.isRequired,
  visibility: PropTypes.string.isRequired,
};

const ExpiredCouponsSection = ({
  className,
  coupons,
  gaEventCategory,
  storeName,
  storeSlug,
}) => (
  <ErrorBoundary>
    <section className={twMerge('flex flex-col gap-8', className)}>
      <div className="flex flex-col gap-2">
        <Heading
          as="h2"
          className="text-2xl text-neutral-low-400 dark:text-neutral-high-200"
          size="size4"
        >
          Cupons expirados de {storeName}
        </Heading>
        <Text
          className="text-neutral-low-300 dark:text-neutral-high-300"
          size="size1"
        >
          {placeholder('descriptions.expiredCouponCanDisclaimer')}
        </Text>
      </div>
      <div className="flex flex-col gap-4">
        {coupons.map(({ couponCode, couponId, couponTitle, visibility }) => (
          <ExpiredCouponCard
            key={couponId}
            couponCode={couponCode}
            couponId={couponId}
            couponTitle={couponTitle}
            gaEventCategory={gaEventCategory}
            storeName={storeName}
            storeSlug={storeSlug}
            visibility={visibility}
          />
        ))}
      </div>
    </section>
  </ErrorBoundary>
);

ExpiredCouponsSection.propTypes = {
  className: PropTypes.string,
  coupons: PropTypes.arrayOf(
    PropTypes.shape({
      couponCode: PropTypes.string,
      couponId: PropTypes.number.isRequired,
      couponTitle: PropTypes.string.isRequired,
      visibility: PropTypes.string.isRequired,
    })
  ).isRequired,
  gaEventCategory: PropTypes.string.isRequired,
  storeName: PropTypes.string.isRequired,
  storeSlug: PropTypes.string.isRequired,
};

export default ExpiredCouponsSection;
